/**
 * useResolvedStepFlow — SINGLE SOURCE OF TRUTH for the PF revamp step sequence
 * and per-step navigation targets.
 *
 * The revamp's 5 nominal steps (catalogue → configuration → instructions →
 * billing → summary) are NOT all always present:
 *   - `configuration` is in the sequence only when the cart holds at least one
 *     configurable item (a shell that resolves to ≥1 configuration flow).
 *   - `instructions` is in the sequence only when `selectInstructionsStepSkipped`
 *     is false.
 *   - `catalogue`, `billing`, `summary` are always present.
 *
 * Several call sites used to re-derive these skip rules independently
 * (StepNavSection labels, navigation targets, the would-be progress stepper),
 * which is exactly what let the catalogue/configuration "Proceed" buttons lie
 * about their destination. This hook centralises the skip-aware ordering plus
 * the next/prev decision so every consumer reads the same truth.
 *
 * The next/prev decision is exported as pure functions (`resolveNextStep` /
 * `resolvePrevStep`) so `useStepNav` can resolve targets for an arbitrary step
 * without coupling to the store's `currentStep`.
 */

import { useMemo } from 'react'

import type { ConfigurationFlowId, PfStep } from 'models/purchaseFlowRevamp'
import { usePfCatalogue } from 'dataQueries/purchaseFlowRevamp.query'
import { useInstructionOptions } from 'dataQueries/purchaseFlowRevampInstructions.query'
import { FeatureFlag, useFeatureFlag } from 'utils/featureFlags'

import { usePfStore } from '../store'
import { selectInstructionsStepSkipped } from '../store/selectors'
import { resolveTabsFromCartAndCatalogue } from '../steps/configuration/resolveTabs'

export interface FirstConfigTarget {
  cartItemId: string
  flowId: ConfigurationFlowId
}

export interface ResolvedStepFlow {
  /** Ordered, skip-aware step list. Always starts with `catalogue` and ends
   *  with `billing`, `summary`. `configuration` is present only when the cart
   *  has a configurable item; `instructions` only when it is not auto-skipped. */
  steps: PfStep[]
  /** Current step, read from the store. */
  currentStep: PfStep
  /** Index of `currentStep` within `steps` (`-1` when the current step is not
   *  part of the skip-aware sequence — impossible during normal navigation). */
  currentIndex: number
  /** The step the primary ("Proceed") action navigates to from `currentStep`
   *  (`null` on summary). */
  nextStep: PfStep | null
  /** The step the secondary ("Back") action navigates to from `currentStep`
   *  (`null` on catalogue). */
  prevStep: PfStep | null
  /** First configurable `(cartItem, flow)` tuple in cart order, or `undefined`
   *  when the cart contains no configurable items. */
  firstConfigTarget?: FirstConfigTarget
}

/** Inputs the next/prev decision needs beyond the originating step. */
export interface StepFlowContext {
  firstConfigTarget?: FirstConfigTarget
  isInstructionsStepSkipped: boolean
}

/**
 * Pure resolver — the step the primary ("Proceed") action navigates to from
 * `from`. Mirrors the legacy `handlePrimary` switch in StepNavSection exactly.
 */
export const resolveNextStep = (
  from: PfStep,
  { firstConfigTarget, isInstructionsStepSkipped }: StepFlowContext
): PfStep | null => {
  switch (from) {
    case 'catalogue':
      if (firstConfigTarget) return 'configuration'
      return isInstructionsStepSkipped ? 'billing' : 'instructions'
    case 'configuration':
      return isInstructionsStepSkipped ? 'billing' : 'instructions'
    case 'instructions':
      return 'billing'
    case 'billing':
      return 'summary'
    case 'summary':
      return null
  }
}

/**
 * Pure resolver — the step the secondary ("Back") action navigates to from
 * `from`. Mirrors the legacy `handleSecondary` switch in StepNavSection exactly,
 * including the W1.5 billing→catalogue smart-back when Instructions is skipped.
 */
export const resolvePrevStep = (
  from: PfStep,
  { firstConfigTarget, isInstructionsStepSkipped }: StepFlowContext
): PfStep | null => {
  switch (from) {
    case 'catalogue':
      return null
    case 'configuration':
      return 'catalogue'
    case 'instructions':
      return firstConfigTarget ? 'configuration' : 'catalogue'
    case 'billing':
      return isInstructionsStepSkipped ? 'catalogue' : 'instructions'
    case 'summary':
      return 'billing'
  }
}

/**
 * Resolves the skip-aware step sequence + the current step's nav targets from
 * the live cart + catalogue + feature flag.
 */
export const useResolvedStepFlow = (): ResolvedStepFlow => {
  const items = usePfStore((s) => s.items)
  const currentStep = usePfStore((s) => s.currentStep)

  // Catalogue powers the configurable-flow lookup + the skip selector.
  const catalogue = usePfCatalogue().data

  // Same fallback gate the rest of the revamp uses — when BE has not yet emitted
  // `shell.configurationFlows`, resolve them FE-side from (productType|kind).
  const allowFallbackFlowResolver = useFeatureFlag(FeatureFlag.ALLOW_PF_REVAMP_FALLBACK_FLOW_RESOLVER)

  // First configurable (cartItem, flow) tuple — drives whether `configuration`
  // is part of the sequence + the catalogue primary's target. Uses the SAME
  // tuple resolver as the configuration page + the config-step validity selector
  // so they never disagree on the flow set.
  const firstConfigTarget = useMemo<FirstConfigTarget | undefined>(() => {
    const [first] = resolveTabsFromCartAndCatalogue(items, catalogue?.shellProducts ?? [], allowFallbackFlowResolver)
    return first ? { cartItemId: first.cartItemId, flowId: first.flowId } : undefined
  }, [items, catalogue, allowFallbackFlowResolver])

  // The BE's exact skip signal. Threaded HERE (not only into the
  // Instructions page) because this hook is what `PfProgressStepper` and
  // `useStepNav` read on EVERY step: without it the stepper would keep showing a
  // step the BE hides and the "Proceed" labels would lie about their target.
  // Cost is one extra early POST, self-gated on ws + session + a non-empty cart
  // and sharing the Instructions page's cache entry (5-min staleTime).
  // `visible === undefined` while loading ⇒ the cart-shape approximation, i.e.
  // exactly today's behaviour.
  const instructionsVisible = useInstructionOptions().data?.visible

  const isInstructionsStepSkipped = usePfStore(
    useMemo(() => selectInstructionsStepSkipped(catalogue, instructionsVisible), [catalogue, instructionsVisible])
  )

  return useMemo(() => {
    const steps: PfStep[] = ['catalogue']
    if (firstConfigTarget) steps.push('configuration')
    if (!isInstructionsStepSkipped) steps.push('instructions')
    steps.push('billing', 'summary')

    const context: StepFlowContext = { firstConfigTarget, isInstructionsStepSkipped }

    return {
      steps,
      currentStep,
      currentIndex: steps.indexOf(currentStep),
      nextStep: resolveNextStep(currentStep, context),
      prevStep: resolvePrevStep(currentStep, context),
      firstConfigTarget,
    }
  }, [currentStep, firstConfigTarget, isInstructionsStepSkipped])
}
